import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, api, can, getUser } from '../lib/api';
import type { Page, Site } from '../lib/types';
import {
  Badge, Banner, Button, Card, CopyButton, Empty, Field, LoadError, Skeleton, Toast, input, select,
} from '../lib/ui';
import { regimeLabel, regimeLong, roleHint, roleLabel } from '../lib/labels';
import { useConfirm } from '../lib/Modal';
import { Hilfe } from '../features/help/Help';
import { fmtDate, t } from '../lib/i18n';

/**
 * Team and invitations (Doc 04 §8).
 *
 * Roles are shown with what they mean rather than by name alone: "editor" tells nobody
 * that the point of the role is being unable to publish.
 */
interface Member {
  id: string;
  email: string;
  name: string;
  role: string;
  site_ids: string[];
  last_seen_at: string | null;
}
interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  role: string;
  last_used_at: string | null;
  expires_at: string | null;
  secret?: string;
}
interface TenantSettings {
  default_regime: string;
}
interface Invitation {
  id: string;
  email: string;
  role: string;
  site_ids: string[];
  expires_at: string;
  token?: string;
}

const assignable = (role?: string): string[] =>
  role === 'tenant_owner' ? ['tenant_owner', 'tenant_admin', 'editor', 'analyst']
  : role === 'tenant_admin' ? ['editor', 'analyst']
  : [];

export function Team() {
  const qc = useQueryClient();
  const me = getUser();
  const [invite, setInvite] = useState({ email: '', role: 'editor', site_ids: [] as string[] });
  const [fresh, setFresh] = useState<Invitation | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [confirmNode, confirm] = useConfirm();

  const members = useQuery({ queryKey: ['members'], queryFn: () => api<Page<Member>>('/v1/team/members') });
  const invites = useQuery({ queryKey: ['invites'], queryFn: () => api<Page<Invitation>>('/v1/team/invitations') });
  const sites = useQuery({ queryKey: ['sites'], queryFn: () => api<Page<Site>>('/v1/sites') });
  const keys = useQuery({ queryKey: ['apikeys'], queryFn: () => api<Page<ApiKey>>('/v1/team/api-keys') });
  const settings = useQuery({
    queryKey: ['tenant-settings'],
    queryFn: () => api<TenantSettings>('/v1/team/settings'),
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['members'] });
    void qc.invalidateQueries({ queryKey: ['invites'] });
    void qc.invalidateQueries({ queryKey: ['apikeys'] });
  };

  const [keyName, setKeyName] = useState('');
  const [keyRole, setKeyRole] = useState('analyst');
  const [freshKey, setFreshKey] = useState<ApiKey | null>(null);

  const mintKey = useMutation({
    mutationFn: () => api<ApiKey>('/v1/team/api-keys', {
      method: 'POST',
      body: { name: keyName, role: keyRole, expires_in_days: 365 },
    }),
    onSuccess: (k) => { setFreshKey(k); setKeyName(''); refresh(); },
    onError: (e) => setProblem(teamProblem(e)),
  });
  const revokeKey = useMutation({
    mutationFn: (id: string) => api(`/v1/team/api-keys/${id}`, { method: 'DELETE' }),
    onSuccess: () => { setDone(t('Schlüssel widerrufen.')); refresh(); },
    onError: (e) => setProblem(teamProblem(e)),
  });

  const setRegime = useMutation({
    mutationFn: (default_regime: string) =>
      api<TenantSettings>('/v1/team/settings', { method: 'PUT', body: { default_regime } }),
    onSuccess: (v) => {
      qc.setQueryData(['tenant-settings'], v);
      setProblem(null);
      setDone(t('Rückfall-Regime geändert. Es gilt ab dem nächsten Veröffentlichen.'));
    },
    onError: (e) => setProblem(teamProblem(e)),
  });

  const send = useMutation({
    mutationFn: () => api<Invitation>('/v1/team/invitations', { method: 'POST', body: invite }),
    onSuccess: (inv) => {
      setFresh(inv);
      setInvite({ email: '', role: 'editor', site_ids: [] });
      setProblem(null);
      refresh();
    },
    onError: (e) => setProblem(teamProblem(e)),
  });
  const update = useMutation({
    mutationFn: (v: { id: string; role: string; site_ids: string[] }) =>
      api(`/v1/team/members/${v.id}`, { method: 'PATCH', body: { role: v.role, site_ids: v.site_ids } }),
    onSuccess: () => { setProblem(null); setDone(t('Rolle geändert.')); refresh(); },
    onError: (e) => setProblem(teamProblem(e)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/v1/team/members/${id}`, { method: 'DELETE' }),
    onSuccess: () => { setProblem(null); setDone(t('Mitglied entfernt.')); refresh(); },
    onError: (e) => setProblem(teamProblem(e)),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api(`/v1/team/invitations/${id}`, { method: 'DELETE' }),
    onSuccess: () => { setDone(t('Einladung zurückgezogen.')); refresh(); },
    onError: (e) => setProblem(teamProblem(e)),
  });

  const writable = can('user:write');
  const manageable = can('tenant:manage');
  const roleOptions = assignable(me?.role);
  const siteName = (id: string) => sites.data?.data.find((s) => s.id === id)?.name ?? id.slice(0, 8);

  return (
    <div className="space-y-4">
      {confirmNode}
      {done && <Toast message={done} onDone={() => setDone(null)} />}
      <h1 className="text-xl font-semibold">{t('Team')}</h1>
      {problem && <Banner kind="error">{problem}</Banner>}

      <Card title={t('Mitglieder')} count={members.data?.data.length}
        actions={<Hilfe to="rollen" />}>
        {members.isPending && <Skeleton rows={3} />}
        {members.isError && (
          <LoadError what={t('Die Mitgliederliste')} onRetry={() => void members.refetch()} />
        )}
        <ul className="divide-y divide-[var(--color-line)]">
          {(members.data?.data ?? []).map((m) => (
            <li key={m.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
              <div className="min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="text-[13px] font-medium">{m.name || m.email}</span>
                  {m.name && <span className="text-xs text-[var(--color-muted)]">{m.email}</span>}
                  {m.email === me?.email && (
                    <span className="rounded-[2px] bg-[var(--color-sunken)] px-1.5 text-[11px]">{t('Sie')}</span>
                  )}
                </div>
                <div className="mt-0.5 text-xs text-[var(--color-muted)]">
                  {m.site_ids.length === 0
                    ? t('Alle Websites')
                    : m.site_ids.map(siteName).join(', ')}
                  {' · '}
                  {m.last_seen_at
                    ? t('zuletzt {date}', { date: fmtDate(m.last_seen_at) })
                    : t('noch nie angemeldet')}
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-3">
                {writable && roleOptions.includes(m.role) ? (
                  <select
                    className={select} value={m.role} title={roleHint(m.role)}
                    onChange={(e) => update.mutate({ id: m.id, role: e.target.value, site_ids: m.site_ids })}
                  >
                    {roleOptions.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
                  </select>
                ) : (
                  <Badge title={roleHint(m.role)}>{roleLabel(m.role)}</Badge>
                )}
                {writable && (
                  <button className="text-xs text-[var(--color-danger)] hover:underline"
                    onClick={() => confirm({
                      title: t('Mitglied entfernen?'),
                      danger: true,
                      confirmLabel: t('Entfernen'),
                      body: <>{t('{who} verliert sofort den Zugang zu allen Websites dieses Kontos. Eine neue Einladung stellt ihn wieder her.', { who: m.name || m.email })}</>,
                      onConfirm: () => remove.mutate(m.id),
                    })}>
                    {t('Entfernen')}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </Card>

      {(invites.data?.data.length ?? 0) > 0 && (
        <Card title={t('Offene Einladungen')} count={invites.data!.data.length}>
          <ul className="divide-y divide-[var(--color-line)]">
            {invites.data!.data.map((i) => (
              <li key={i.id} className="flex items-center justify-between gap-3 py-2 text-[13px]">
                <span>
                  {i.email}
                  <span className="ml-2 text-xs text-[var(--color-muted)]">
                    {t('{role} · läuft ab {date}', { role: roleLabel(i.role), date: fmtDate(i.expires_at) })}
                  </span>
                </span>
                {writable && (
                  <button className="text-xs text-[var(--color-danger)] hover:underline"
                    onClick={() => confirm({
                      title: t('Einladung zurückziehen?'),
                      danger: true,
                      confirmLabel: t('Zurückziehen'),
                      body: <>{t('Der Link an {email} funktioniert danach nicht mehr.', { email: i.email })}</>,
                      onConfirm: () => revoke.mutate(i.id),
                    })}>
                    {t('Zurückziehen')}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {writable && (
        <Card title={t('Einladen')} actions={<Hilfe to="einladungen" />}>
          {fresh?.token && (
            <div className="mb-4 space-y-2 rounded-[3px] border border-[var(--color-line)] bg-[var(--color-note)] p-3">
              <p className="text-[13px] text-[var(--color-note-ink)]">
                {t('Einladungslink für {email} — er wird nur dieses eine Mal angezeigt und ist sieben Tage gültig.', { email: fresh.email })}
              </p>
              {/* Shown rather than emailed: there is no mail delivery yet, and a link that
                  exists only in a log nobody reads would be worse than one to copy. */}
              <code className="block overflow-x-auto rounded-[2px] bg-[var(--color-surface)] p-2 text-[11px]">
                {inviteLink(fresh.token)}
              </code>
              <div className="flex items-center gap-3">
                <CopyButton value={inviteLink(fresh.token)} label={t('Link kopieren')} />
                <button className="text-xs text-[var(--color-muted)] hover:underline"
                  onClick={() => setFresh(null)}>
                  {t('Ich habe ihn gespeichert')}
                </button>
              </div>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto]">
            <Field label={t('E-Mail')}>
              <input className={input} type="email" value={invite.email}
                placeholder="kollege@firma.de"
                onChange={(e) => setInvite({ ...invite, email: e.target.value })} />
            </Field>
            <Field label={t('Rolle')} hint={roleHint(invite.role)}>
              <select className={input} value={invite.role}
                onChange={(e) => setInvite({ ...invite, role: e.target.value })}>
                {roleOptions.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
              </select>
            </Field>
            <div className="flex items-end">
              <Button disabled={!invite.email.includes('@') || send.isPending}
                onClick={() => send.mutate()}>
                {t('Einladen')}
              </Button>
            </div>
          </div>

          <div className="mt-3">
            <div className="eyebrow mb-1.5">{t('Websites')}</div>
            <p className="mb-2 text-xs text-[var(--color-muted)]">
              {t('Nichts auswählen heißt alle — auch die, die es noch nicht gibt.')}
            </p>
            <div className="flex flex-wrap gap-3">
              {(sites.data?.data ?? []).map((s) => (
                <label key={s.id} className="flex items-center gap-1.5 text-[13px]">
                  <input type="checkbox" checked={invite.site_ids.includes(s.id)}
                    onChange={(e) => setInvite({
                      ...invite,
                      site_ids: e.target.checked
                        ? [...invite.site_ids, s.id]
                        : invite.site_ids.filter((x) => x !== s.id),
                    })} />
                  {s.name}
                </label>
              ))}
            </div>
          </div>
        </Card>
      )}
      {writable && (
        <Card title={t('API-Schlüssel')} count={keys.data?.data.length ?? 0}
          actions={<Hilfe to="api-schluessel" />}>
          <p className="mb-3 text-xs text-[var(--color-muted)]">
            {t('Für Skripte und Integrationen. Ein Schlüssel gehört dem Tenant, nicht einer Person — deshalb kann er nur lesen und auswerten, nie Zugänge verändern.')}
          </p>

          {freshKey?.secret && (
            <div className="mb-4 space-y-2 rounded-[3px] border border-[var(--color-line)] bg-[var(--color-note)] p-3">
              <p className="text-[13px] text-[var(--color-note-ink)]">
                {t('{name} — dieser Schlüssel wird jetzt einmal angezeigt und ist danach nicht mehr auslesbar.', { name: freshKey.name })}
              </p>
              <code className="block overflow-x-auto rounded-[2px] bg-[var(--color-surface)] p-2 text-[11px]">
                {freshKey.secret}
              </code>
              <div className="flex items-center gap-3">
                <CopyButton value={freshKey.secret} label={t('Schlüssel kopieren')} />
                <button className="text-xs text-[var(--color-muted)] hover:underline"
                  onClick={() => setFreshKey(null)}>
                  {t('Ich habe ihn gespeichert')}
                </button>
              </div>
            </div>
          )}

          {(keys.data?.data.length ?? 0) > 0 && (
            <ul className="mb-4 divide-y divide-[var(--color-line)]">
              {keys.data!.data.map((k) => (
                <li key={k.id} className="flex items-center justify-between gap-3 py-2 text-[13px]">
                  <span>
                    {k.name}
                    <span className="ml-2 font-mono text-[11px] text-[var(--color-muted)]">
                      cmpk_live_{k.prefix}…
                    </span>
                    <span className="ml-2 text-xs text-[var(--color-muted)]">
                      {roleLabel(k.role)} · {k.last_used_at
                        ? t('zuletzt {date}', { date: fmtDate(k.last_used_at) })
                        : t('nie benutzt')}
                    </span>
                  </span>
                  <button className="text-xs text-[var(--color-danger)] hover:underline"
                    onClick={() => confirm({
                      title: t('Schlüssel widerrufen?'),
                      danger: true,
                      confirmLabel: t('Widerrufen'),
                      body: <>{t('Jede Integration, die {name} benutzt, bekommt ab sofort 401. Ein widerrufener Schlüssel lässt sich nicht reaktivieren.', { name: k.name })}</>,
                      onConfirm: () => revokeKey.mutate(k.id),
                    })}>
                    {t('Widerrufen')}
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto]">
            <Field label={t('Name')} hint={t('Wofür ist er da? Das steht später in der Liste.')}>
              <input className={input} value={keyName} placeholder={t('Reporting-Skript')}
                onChange={(e) => setKeyName(e.target.value)} />
            </Field>
            <Field label={t('Rolle')}>
              <select className={input} value={keyRole} onChange={(e) => setKeyRole(e.target.value)}>
                <option value="analyst">{t('Auswertung — lesen und auswerten')}</option>
                <option value="api_client">{t('Integration — nur Websites und Statistik')}</option>
              </select>
            </Field>
            <div className="flex items-end">
              <Button variant="secondary" disabled={!keyName.trim() || mintKey.isPending}
                onClick={() => mintKey.mutate()}>
                {t('Erstellen')}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* The tenant's own setting, not a team one — but Team is where account-wide things
          already live, and it was worth less than a route of its own. It sits here rather
          than on a site page because it applies to every site at once. */}
      <Card title={t('Rückfall-Regime')} actions={<Hilfe to="rechtslage" />}>
        <p className="mb-3 text-xs text-[var(--color-muted)]">
          {t('Gilt für Besucher, deren Land sich nicht bestimmen lässt — zurzeit für alle, weil pro Website nur eine Variante ausgeliefert wird. Eine Änderung wirkt ab dem nächsten Veröffentlichen der jeweiligen Website.')}
        </p>
        {settings.isPending && <Skeleton rows={1} />}
        {settings.isError && (
          <LoadError what={t('Das Rückfall-Regime')} onRetry={() => void settings.refetch()} />
        )}
        {settings.data && (
          <div className="max-w-sm">
            <Field label={t('Regime')}
              hint={manageable ? undefined : t('Nur die Inhaberin oder der Inhaber des Kontos kann das ändern.')}>
              <select
                className={select}
                disabled={!manageable || setRegime.isPending}
                value={settings.data.default_regime}
                onChange={(e) => {
                  const v = e.target.value;
                  // Only "none" gets a confirmation. The others are all a real legal
                  // footing; "none" is the one that says nobody has to be asked at all.
                  if (v === 'none') {
                    confirm({
                      title: t('Ohne Regime ausliefern?'),
                      danger: true,
                      confirmLabel: t('Trotzdem setzen'),
                      body: <>{t('Besucher, deren Land unbekannt ist, werden dann ohne Einwilligungspflicht behandelt — es wird nichts vorab blockiert. Innerhalb der EU ist das in aller Regel falsch.')}</>,
                      onConfirm: () => setRegime.mutate(v),
                    });
                    return;
                  }
                  setRegime.mutate(v);
                }}
              >
                {['gdpr_de', 'gdpr_eu', 'ch_dsg', 'ccpa', 'none'].map((r) => (
                  <option key={r} value={r} title={regimeLong(r)}>{regimeLabel(r)}</option>
                ))}
              </select>
            </Field>
          </div>
        )}
      </Card>
    </div>
  );
}

const inviteLink = (token: string): string => `${location.origin}/#/einladung?token=${token}`;

function teamProblem(e: unknown): string {
  if (!(e instanceof ApiError)) return t('Fehlgeschlagen.');
  const codes = (e.problem.errors ?? []).map((x) => x.code);
  if (codes.includes('last_owner')) {
    return t('Der letzte Owner kann nicht entfernt oder herabgestuft werden — danach könnte niemand mehr jemanden ernennen.');
  }
  if (codes.includes('already_member')) return t('Diese Person ist bereits im Team.');
  if (codes.includes('not_assignable')) return t('Diese Rolle können Sie nicht vergeben.');
  if (codes.includes('invalid')) return t('Die E-Mail-Adresse sieht nicht richtig aus.');
  return t('Fehlgeschlagen.');
}
